// ===== Checkout with Bank Transfer and Invoice payment methods =====
// Flow: Fulfillment → Payment → Summary (where order is placed)
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import axiosInstance from "../../auth/axiosWithInterceptor";
import { Card, CardHeader, CardTitle, CardContent } from "../../lib/components/ui/card";
import { Button } from "../../lib/components/ui/button";
import { Input } from "../../lib/components/ui/input";
import { Label } from "../../lib/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../lib/components/ui/select";
import { Skeleton } from "../../lib/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Building2, Banknote, AlertTriangle, CheckCircle, Copy } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "../../auth/useAuth";
import { useCart } from "@/features/cart/hooks/useCart";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "@/lib/format";
import type { CartItem } from "@/types/catalog";

function CheckoutSkeleton() {
  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Title + step indicator */}
      <div className="mb-6 space-y-3">
        <Skeleton className="h-7 w-32" />
        <div className="flex items-center space-x-2">
          <Skeleton className="h-7 w-28 rounded-full" />
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-7 w-24 rounded-full" />
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-7 w-24 rounded-full" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main step card — mirrors the FulfillmentStep card */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-44" />
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Radio options */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Skeleton className="h-4 w-4 rounded-full" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <div className="flex items-center space-x-3">
                  <Skeleton className="h-4 w-4 rounded-full" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
              {/* Shipping method select */}
              <div className="space-y-2">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-10 w-full rounded-md" />
              </div>
              {/* Address form — 2-column grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-9 w-full rounded-md" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Order summary sidebar */}
        <div>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex justify-between">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-14" />
                </div>
              ))}
              <hr />
              <div className="flex justify-between">
                <Skeleton className="h-5 w-12" />
                <Skeleton className="h-5 w-16" />
              </div>
              <Skeleton className="h-3 w-48" />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Back / Continue buttons */}
      <div className="flex justify-between mt-6">
        <Skeleton className="h-10 w-28 rounded-md" />
        <Skeleton className="h-10 w-32 rounded-md" />
      </div>
    </div>
  );
}

type Cart = {
  id: number;
  items: CartItem[];
};

/** Returns the display label for any cart item type. */
function getCartItemLabel(item: CartItem): string {
  if (item.item_type === "PRODUCT") {
    return item.item_name ?? `Product #${item.product_code ?? item.product_id ?? "?"}`;
  }
  if (item.item_type === "RETAIL_FILAMENT") {
    const parts = [
      item.filament_brand,
      item.filament_material,
      item.filament_color_name ? `– ${item.filament_color_name}` : null,
      item.filament_weight_grams ? `(${item.filament_weight_grams}g)` : null,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : "Filament";
  }
  // MODEL_FILE (default)
  return item.model_file_name ?? "Model";
}

type StoreSettings = {
  currency: string;
  tax_rate: number;
  allow_pickup: boolean;
  pickup_location: string | null;
  bank_transfer_entity: string | null;
  bank_transfer_bank: string | null;
  bank_transfer_iban: string | null;
  bank_transfer_bic_swift: string | null;
};

type ShippingMethod = {
  id: number;
  name: string;
  price: number;
  enabled: boolean;
};

type ShippingContact = {
  full_name: string;
  email: string;
  address_line1: string;
  address_line2: string;
  city: string;
  postal_code: string;
  country: string;
};

type CheckoutTotals = {
  itemsSubtotal: number;
  shipping: number;
  tax: number;
  total: number;
};

// Updated step order: fulfillment -> payment -> summary -> confirmation
type CheckoutStep = "fulfillment" | "payment" | "summary" | "confirmation";

type PaymentMethodType = "BANK_TRANSFER" | "INVOICE";

// --- START EDIT: OrderResponse type updated to handle backend returning numeric fields as strings ---
type OrderResponse = {
  id: number;
  order_code: string;
  total_price: number | string;
  shipping_cost?: number | string;
  tax_amount?: number | string;
  payment_status: string;
  payment_method: string;
  fulfillment_method: string;
};
// --- END EDIT ---

// --- START EDIT: Helper function to safely convert numeric fields to numbers ---
function toSafeNumber(value: unknown, fallback: number = 0): number {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  }
  return fallback;
}
// --- END EDIT ---

function getApiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    if (data && typeof data === "object" && "detail" in data) {
      const detail = (data as { detail?: string }).detail;
      if (typeof detail === "string" && detail.length > 0) return detail;
    }
    return err.message || fallback;
  }
  return fallback;
}

// Shared utility for copying to clipboard
function copyToClipboard(text: string, successMessage: string) {
  navigator.clipboard.writeText(text);
  toast.success(successMessage);
}

type StoreSettingsApi = {
  currency: string;
  tax_rate: number | string;
  allow_pickup: boolean;
  pickup_location: string | null;
  bank_transfer_entity?: string | null;
  bank_transfer_bank?: string | null;
  bank_transfer_iban?: string | null;
  bank_transfer_bic_swift?: string | null;
};

type ShippingMethodApi = {
  id: number;
  name: string;
  price: number | string | null;
  enabled: boolean;
};

type PickupPoint = {
  code: string | null;
  name: string | null;
  address: {
    line1: string | null;
    postal_code: string | null;
    city: string | null;
    country: string;
  };
  type?: string | null;
};

export default function CheckoutPage() {
  const { t } = useTranslation('checkout');
  const { user } = useAuth();
  const { reset: resetCart, refresh: refreshCart } = useCart();
  const [currentStep, setCurrentStep] = useState<CheckoutStep>("fulfillment");
  const [cart, setCart] = useState<Cart | null>(null);
  const [settings, setSettings] = useState<StoreSettings | null>(null);
  const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
  const [loading, setLoading] = useState(true);

  const isCompany = user?.is_company === true;

  const [fulfillmentMethod, setFulfillmentMethod] = useState<"SHIPPING" | "PICKUP">("SHIPPING");
  const [shippingMethodId, setShippingMethodId] = useState<number | null>(null);
  const [shippingContact, setShippingContact] = useState<ShippingContact>({
    full_name: "",
    email: user?.email || "",
    address_line1: "",
    address_line2: "",
    city: "",
    postal_code: "",
    country: "PT",
  });

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType>("BANK_TRANSFER");
  const [createdOrder, setCreatedOrder] = useState<OrderResponse | null>(null);

  const [pickupSearchPostal, setPickupSearchPostal] = useState<string>("");
  const [pickupSearchCity, setPickupSearchCity] = useState<string>("");
  const [pickupPoints, setPickupPoints] = useState<PickupPoint[]>([]);
  const [pickupLoading, setPickupLoading] = useState<boolean>(false);
  const [selectedPickupCode, setSelectedPickupCode] = useState<string | null>(null);
  const [selectedPickup, setSelectedPickup] = useState<PickupPoint | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (isCompany) {
      setFulfillmentMethod("PICKUP");
      setPaymentMethod("INVOICE");
    } else {
      setPaymentMethod("BANK_TRANSFER");
    }
  }, [isCompany]);

  const fetchData = async () => {
    try {
      const [cartRes, settingsRes] = await Promise.all([
        axiosInstance.get<Cart>("/cart"),
        axiosInstance.get<StoreSettingsApi>("/store/settings"),
      ]);

      setCart(cartRes.data);

      const normalizedSettings: StoreSettings = {
        currency: settingsRes.data.currency,
        tax_rate:
          typeof settingsRes.data.tax_rate === "string"
            ? parseFloat(settingsRes.data.tax_rate)
            : settingsRes.data.tax_rate,
        allow_pickup: !!settingsRes.data.allow_pickup,
        pickup_location: settingsRes.data.pickup_location,
        bank_transfer_entity: settingsRes.data.bank_transfer_entity || "OLIVEIRA & PENEDA, LDA",
        bank_transfer_bank: settingsRes.data.bank_transfer_bank || "Caixa Geral Depósitos, SA",
        bank_transfer_iban: settingsRes.data.bank_transfer_iban || "PT50 0035 0447 0008 2312 3309 8",
        bank_transfer_bic_swift: settingsRes.data.bank_transfer_bic_swift || "CGDIPTPL",
      };
      setSettings(normalizedSettings);

      if (cartRes.data?.items?.length > 0) {
        const shippingRes = await axiosInstance.get<ShippingMethodApi[]>("/shipping/methods?country=PT");
        const normalizedMethods: ShippingMethod[] = (shippingRes.data || [])
          .filter((m) => m.name.toLowerCase() !== "pick up in store")
          .map((m) => ({
            id: m.id,
            name: m.name,
            price: m.price == null ? 0 : typeof m.price === "string" ? parseFloat(m.price) : m.price,
            enabled: !!m.enabled,
          }));
        setShippingMethods(normalizedMethods);
      }
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, t("errors.loadFailed")));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const isCttPickupMethodSelected = (): boolean => {
    if (!shippingMethodId) return false;
    const method = shippingMethods.find((m) => m.id === shippingMethodId);
    return !!method && method.name.toLowerCase() === "ctt pickup point";
  };

  useEffect(() => {
    if (!isCttPickupMethodSelected()) {
      setPickupPoints([]);
      setSelectedPickupCode(null);
      setSelectedPickup(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingMethodId, shippingMethods]);

  const calculateTotals = (): CheckoutTotals => {
    if (!cart || !settings) return { itemsSubtotal: 0, shipping: 0, tax: 0, total: 0 };
    const itemsSubtotal = cart.items.reduce((sum, item) => sum + item.subtotal, 0);
    let shippingCost = 0;
    if (fulfillmentMethod === "SHIPPING" && shippingMethodId) {
      const method = shippingMethods.find((m) => m.id === shippingMethodId);
      shippingCost = method?.price ?? 0;
    }
    const taxAmount = settings.tax_rate * (itemsSubtotal + shippingCost);
    const total = itemsSubtotal + shippingCost + taxAmount;
    return { itemsSubtotal, shipping: shippingCost, tax: taxAmount, total };
  };

  // Updated step navigation: fulfillment -> payment -> summary
  const handleNext = () => {
    if (currentStep === "fulfillment") {
      if (fulfillmentMethod === "SHIPPING") {
        if (!shippingMethodId) { toast.error(t("errors.noShipping")); return; }
        if (isCttPickupMethodSelected()) {
          if (!selectedPickup || !selectedPickup.code) { toast.error(t("errors.noPickup")); return; }
        } else {
          if (!shippingContact.full_name || !shippingContact.email || !shippingContact.address_line1 || !shippingContact.city || !shippingContact.postal_code) {
            toast.error(t("errors.invalidAddress")); return;
          }
        }
      } else if (fulfillmentMethod === "PICKUP") {
        if (!shippingContact.full_name || !shippingContact.email) { toast.error(t("errors.pickupContactRequired")); return; }
      }
      setCurrentStep("payment");
    } else if (currentStep === "payment") {
      setCurrentStep("summary");
    }
  };

  const handleBack = () => {
    if (currentStep === "payment") setCurrentStep("fulfillment");
    else if (currentStep === "summary") setCurrentStep("payment");
    else if (currentStep === "confirmation") navigate("/account/orders");
  };

  const handlePickupSearch = async () => {
    if (!pickupSearchPostal || pickupSearchPostal.trim().length < 4) { toast.error(t("errors.invalidPostalCode")); return; }
    setPickupLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("postal_code", pickupSearchPostal.trim());
      if (pickupSearchCity.trim()) params.set("city", pickupSearchCity.trim());
      const res = await axiosInstance.get<PickupPoint[]>(`/api/shipping/pickup-points?${params.toString()}`);
      setPickupPoints(res.data || []);
      if ((res.data || []).length === 0) toast.info(t("messages.noPickupPoints"));
    } catch (err: unknown) { toast.error(getApiErrorMessage(err, t("errors.pickupSearchFailed"))); }
    finally { setPickupLoading(false); }
  };

  const handleSelectPickup = (code: string) => {
    setSelectedPickupCode(code);
    setSelectedPickup(pickupPoints.find((x) => x.code === code) || null);
  };

  if (loading) return <CheckoutSkeleton />;

  if (!cart?.items?.length) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Card><CardContent className="text-center py-8"><p>{t('errors.emptyCart')}</p><Button onClick={() => navigate("/")} className="mt-4">{t('confirmation.continueShopping')}</Button></CardContent></Card>
      </div>
    );
  }

  const totals = calculateTotals();

  // Guests who completed checkout see the confirmation step inline
  // Logged-in users are redirected via navigate() in onSuccess callback before reaching here
  if (currentStep === "confirmation" && createdOrder && !user) {
    return <ConfirmationStep order={createdOrder} settings={settings} isCompany={isCompany} onContinueShopping={() => navigate("/")} />;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{t('title')}</h1>
        {/* Updated step indicator: Fulfillment -> Payment -> Summary */}
        <div className="flex items-center mt-2 space-x-2">
          <span className={`px-3 py-1 rounded-full text-sm ${currentStep === "fulfillment" ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"}`}>1. {t('steps.fulfillment')}</span>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <span className={`px-3 py-1 rounded-full text-sm ${currentStep === "payment" ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"}`}>2. {t('steps.payment')}</span>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <span className={`px-3 py-1 rounded-full text-sm ${currentStep === "summary" ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"}`}>3. {t('steps.summary')}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {currentStep === "fulfillment" && (
            <FulfillmentStep settings={settings} shippingMethods={shippingMethods} fulfillmentMethod={fulfillmentMethod} setFulfillmentMethod={setFulfillmentMethod} shippingMethodId={shippingMethodId} setShippingMethodId={setShippingMethodId} shippingContact={shippingContact} setShippingContact={setShippingContact} isCttPickupMethodSelected={isCttPickupMethodSelected()} pickupSearchPostal={pickupSearchPostal} setPickupSearchPostal={setPickupSearchPostal} pickupSearchCity={pickupSearchCity} setPickupSearchCity={setPickupSearchCity} pickupLoading={pickupLoading} pickupPoints={pickupPoints} selectedPickupCode={selectedPickupCode} setSelectedPickupCode={handleSelectPickup} onSearchPickup={handlePickupSearch} selectedPickup={selectedPickup} isCompany={isCompany} />
          )}
          {currentStep === "payment" && (
            <PaymentStep paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} isCompany={isCompany} settings={settings} totals={totals} />
          )}
          {currentStep === "summary" && settings && (
            <SummaryStep 
              cart={cart} 
              settings={settings} 
              fulfillmentMethod={fulfillmentMethod} 
              shippingMethods={shippingMethods} 
              shippingMethodId={shippingMethodId} 
              shippingContact={shippingContact} 
              selectedPickup={selectedPickup} 
              paymentMethod={paymentMethod} 
              isCompany={isCompany} 
              totals={totals}
              onSuccess={(order) => { 
                // --- START: Optimistic cart badge reset + server reconciliation ---
                resetCart();
                refreshCart();
                // --- END: Optimistic cart badge reset ---
                
                if (user) {
                  // Logged-in user: show toast and redirect to orders page with highlight
                  toast.success(t("messages.orderPlaced", { code: order.order_code }));
                  navigate(`/account/orders?highlight=${order.id}`);
                } else {
                  // Guest: show inline confirmation step
                  setCreatedOrder(order); 
                  setCurrentStep("confirmation"); 
                }
              }} 
            />
          )}
        </div>
        <div><OrderSummary totals={totals} settings={settings} /></div>
      </div>

      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={handleBack} disabled={currentStep === "fulfillment"}><ChevronLeft className="w-4 h-4 mr-2" />{t('buttons.back')}</Button>
        {currentStep !== "summary" && currentStep !== "confirmation" && (<Button onClick={handleNext}>{t('buttons.continue')}<ChevronRight className="w-4 h-4 ml-2" /></Button>)}
      </div>
    </div>
  );
}

type FulfillmentStepProps = {
  settings: StoreSettings | null;
  shippingMethods: ShippingMethod[];
  fulfillmentMethod: "SHIPPING" | "PICKUP";
  setFulfillmentMethod: React.Dispatch<React.SetStateAction<"SHIPPING" | "PICKUP">>;
  shippingMethodId: number | null;
  setShippingMethodId: React.Dispatch<React.SetStateAction<number | null>>;
  shippingContact: ShippingContact;
  setShippingContact: React.Dispatch<React.SetStateAction<ShippingContact>>;
  isCttPickupMethodSelected: boolean;
  pickupSearchPostal: string;
  setPickupSearchPostal: (v: string) => void;
  pickupSearchCity: string;
  setPickupSearchCity: (v: string) => void;
  pickupLoading: boolean;
  pickupPoints: PickupPoint[];
  selectedPickupCode: string | null;
  setSelectedPickupCode: (code: string) => void;
  onSearchPickup: () => void;
  selectedPickup: PickupPoint | null;
  isCompany: boolean;
};

function FulfillmentStep({ settings, shippingMethods, fulfillmentMethod, setFulfillmentMethod, shippingMethodId, setShippingMethodId, shippingContact, setShippingContact, isCttPickupMethodSelected, pickupSearchPostal, setPickupSearchPostal, pickupSearchCity, setPickupSearchCity, pickupLoading, pickupPoints, selectedPickupCode, setSelectedPickupCode, onSearchPickup, selectedPickup, isCompany }: FulfillmentStepProps) {
  const { t } = useTranslation('checkout');
  return (
    <Card>
      <CardHeader><CardTitle>{t('fulfillment.title')}</CardTitle></CardHeader>
      <CardContent className="space-y-6">
        {isCompany && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 dark:bg-blue-950/30 dark:border-blue-800">
            <div className="flex items-center gap-2 text-blue-800 dark:text-blue-300"><Building2 className="w-5 h-5" /><span className="font-medium">{t('fulfillment.companyAccountTitle')}</span></div>
            <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">{t('fulfillment.companyAccountNote')}</p>
          </div>
        )}
        <div className="space-y-4">
          {!isCompany && (
            <div className="flex items-center space-x-2">
              <input type="radio" id="shipping" checked={fulfillmentMethod === "SHIPPING"} onChange={() => setFulfillmentMethod("SHIPPING")} />
              <Label htmlFor="shipping">{t('fulfillment.shipping')}</Label>
            </div>
          )}
          {settings?.allow_pickup && (
            <div className="flex items-start space-x-2">
              <input type="radio" id="pickup" checked={fulfillmentMethod === "PICKUP"} onChange={() => setFulfillmentMethod("PICKUP")} disabled={isCompany} />
              <div>
                <Label htmlFor="pickup">{t('fulfillment.pickup')}</Label>
                {settings.pickup_location && <p className="text-sm text-muted-foreground">{t('fulfillment.location')}: {settings.pickup_location}</p>}
              </div>
            </div>
          )}
        </div>

        {fulfillmentMethod === "SHIPPING" && !isCompany && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('fulfillment.shippingMethod')}</Label>
              <Select value={shippingMethodId?.toString()} onValueChange={(value) => setShippingMethodId(parseInt(value, 10))}>
                <SelectTrigger><SelectValue placeholder={t('fulfillment.selectShipping')} /></SelectTrigger>
                <SelectContent>{shippingMethods.map((method) => (<SelectItem key={method.id} value={method.id.toString()}>{method.name} - €{method.price.toFixed(2)}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            {!isCttPickupMethodSelected ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2"><Label htmlFor="full_name">{t('fulfillment.fullName')} *</Label><Input id="full_name" value={shippingContact.full_name} onChange={(e) => setShippingContact({ ...shippingContact, full_name: e.target.value })} required /></div>
                <div className="space-y-2"><Label htmlFor="email">{t('fulfillment.email')} *</Label><Input id="email" type="email" value={shippingContact.email} onChange={(e) => setShippingContact({ ...shippingContact, email: e.target.value })} required /></div>
                <div className="space-y-2"><Label htmlFor="address_line1">{t('fulfillment.addressLine1')} *</Label><Input id="address_line1" value={shippingContact.address_line1} onChange={(e) => setShippingContact({ ...shippingContact, address_line1: e.target.value })} required /></div>
                <div className="space-y-2"><Label htmlFor="address_line2">{t('fulfillment.addressLine2')}</Label><Input id="address_line2" value={shippingContact.address_line2} onChange={(e) => setShippingContact({ ...shippingContact, address_line2: e.target.value })} /></div>
                <div className="space-y-2"><Label htmlFor="city">{t('fulfillment.city')} *</Label><Input id="city" value={shippingContact.city} onChange={(e) => setShippingContact({ ...shippingContact, city: e.target.value })} required /></div>
                <div className="space-y-2"><Label htmlFor="postal_code">{t('fulfillment.postalCode')} *</Label><Input id="postal_code" value={shippingContact.postal_code} onChange={(e) => setShippingContact({ ...shippingContact, postal_code: e.target.value })} required /></div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label htmlFor="pickup_postal">{t('fulfillment.postalCode')} *</Label><Input id="pickup_postal" value={pickupSearchPostal} onChange={(e) => setPickupSearchPostal(e.target.value)} placeholder="1500-200" required /></div>
                  <div className="space-y-2"><Label htmlFor="pickup_city">{t('fulfillment.cityOptional')}</Label><Input id="pickup_city" value={pickupSearchCity} onChange={(e) => setPickupSearchCity(e.target.value)} placeholder="Lisboa" /></div>
                </div>
                <Button onClick={onSearchPickup} disabled={pickupLoading}>{pickupLoading ? t('fulfillment.searching') : t('fulfillment.searchPickupPoints')}</Button>
                {pickupPoints.length > 0 && (
                  <div className="space-y-2">
                    <Label>{t('fulfillment.selectPickupPoint')}</Label>
                    {pickupPoints.map((p) => (
                      <div key={p.code} className="flex items-center gap-3 p-2 rounded border">
                        <input type="radio" checked={selectedPickupCode === p.code} onChange={() => setSelectedPickupCode(p.code || "")} />
                        <div><div className="font-medium">{p.name}</div><div className="text-sm text-muted-foreground">{p.address.line1} — {p.address.postal_code} {p.address.city}</div></div>
                      </div>
                    ))}
                  </div>
                )}
                {selectedPickup && <div className="mt-2 text-sm"><p className="font-medium">{t('fulfillment.selected', { name: selectedPickup.name })}</p></div>}
              </div>
            )}
          </div>
        )}

        {fulfillmentMethod === "PICKUP" && (
          <div className="space-y-4 mt-4">
            <h3 className="font-medium">{t('fulfillment.contactForPickup')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label htmlFor="pickup_name">{t('fulfillment.fullName')} *</Label><Input id="pickup_name" value={shippingContact.full_name} onChange={(e) => setShippingContact({ ...shippingContact, full_name: e.target.value })} required /></div>
              <div className="space-y-2"><Label htmlFor="pickup_email">{t('fulfillment.email')} *</Label><Input id="pickup_email" type="email" value={shippingContact.email} onChange={(e) => setShippingContact({ ...shippingContact, email: e.target.value })} required /></div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

type PaymentStepProps = { 
  paymentMethod: PaymentMethodType; 
  setPaymentMethod: React.Dispatch<React.SetStateAction<PaymentMethodType>>; 
  isCompany: boolean; 
  settings: StoreSettings | null; 
  totals: CheckoutTotals;
};

function PaymentStep({ paymentMethod, setPaymentMethod, isCompany, settings, totals }: PaymentStepProps) {
  const { t } = useTranslation('checkout');
  // --- START EDIT: Helper function for keyboard accessibility on payment tiles ---
  const handleTileKeyDown = (method: PaymentMethodType) => (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setPaymentMethod(method);
    }
  };
  // --- END EDIT ---

  return (
    <Card>
      <CardHeader><CardTitle>{t('payment.title')}</CardTitle></CardHeader>
      <CardContent className="space-y-6">
        {/* --- START EDIT: Improved payment method tile styling for dark mode readability --- */}
        <div className="space-y-4">
          {isCompany && (
            <div 
              role="radio"
              aria-checked={paymentMethod === "INVOICE"}
              tabIndex={0}
              onKeyDown={handleTileKeyDown("INVOICE")}
              className={`
                bg-card border rounded-lg p-4 cursor-pointer transition-all
                focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background
                ${paymentMethod === "INVOICE" 
                  ? "ring-2 ring-primary bg-accent/50 border-primary/60" 
                  : "border-border hover:border-muted-foreground/50 hover:bg-muted/30"
                }
              `} 
              onClick={() => setPaymentMethod("INVOICE")}
            >
              <div className="flex items-center gap-3">
                <input 
                  type="radio" 
                  checked={paymentMethod === "INVOICE"} 
                  onChange={() => setPaymentMethod("INVOICE")} 
                  className="w-4 h-4 accent-primary" 
                  tabIndex={-1}
                  aria-hidden="true"
                />
                <Building2 className={`w-5 h-5 ${paymentMethod === "INVOICE" ? "text-primary" : "text-muted-foreground"}`} />
                <div>
                  <Label className={`cursor-pointer text-foreground ${paymentMethod === "INVOICE" ? "font-semibold" : "font-medium"}`}>
                    {t('payment.invoiceLabel')}
                  </Label>
                  <p className="text-sm text-muted-foreground">{t('payment.invoiceSubtitle')}</p>
                </div>
              </div>
            </div>
          )}
          {!isCompany && (
            <div 
              role="radio"
              aria-checked={paymentMethod === "BANK_TRANSFER"}
              tabIndex={0}
              onKeyDown={handleTileKeyDown("BANK_TRANSFER")}
              className={`
                bg-card border rounded-lg p-4 cursor-pointer transition-all
                focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background
                ${paymentMethod === "BANK_TRANSFER" 
                  ? "ring-2 ring-primary bg-accent/50 border-primary/60" 
                  : "border-border hover:border-muted-foreground/50 hover:bg-muted/30"
                }
              `} 
              onClick={() => setPaymentMethod("BANK_TRANSFER")}
            >
              <div className="flex items-center gap-3">
                <input 
                  type="radio" 
                  checked={paymentMethod === "BANK_TRANSFER"} 
                  onChange={() => setPaymentMethod("BANK_TRANSFER")} 
                  className="w-4 h-4 accent-primary" 
                  tabIndex={-1}
                  aria-hidden="true"
                />
                <Banknote className={`w-5 h-5 ${paymentMethod === "BANK_TRANSFER" ? "text-primary" : "text-muted-foreground"}`} />
                <div>
                  <Label className={`cursor-pointer text-foreground ${paymentMethod === "BANK_TRANSFER" ? "font-semibold" : "font-medium"}`}>
                    {t('payment.bankTransfer')}
                  </Label>
                  <p className="text-sm text-muted-foreground">{t('payment.bankTransferSubtitle')}</p>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* --- END EDIT --- */}

        {/* Bank Transfer Details */}
        {!isCompany && paymentMethod === "BANK_TRANSFER" && settings && (
          <div className="bg-card border border-border rounded-lg p-5 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Banknote className="w-5 h-5 text-primary" />
              <h4 className="font-semibold text-foreground text-lg">{t('payment.bankTransferDetailsTitle')}</h4>
            </div>
            <p className="text-sm text-muted-foreground">{t('payment.bankTransferDetailsNote')}</p>
            <div className="space-y-3 pt-2">
              <div className="flex justify-between items-center py-2.5 border-b border-border">
                <span className="text-muted-foreground">{t('payment.entity')}:</span>
                <span className="font-medium text-foreground">{settings.bank_transfer_entity}</span>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-border">
                <span className="text-muted-foreground">{t('payment.bank')}:</span>
                <span className="font-medium text-foreground">{settings.bank_transfer_bank}</span>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-border">
                <span className="text-muted-foreground">{t('payment.iban')}:</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-foreground">{settings.bank_transfer_iban}</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => copyToClipboard(settings.bank_transfer_iban || "", t("messages.copiedToClipboard"))}
                    className="h-8 w-8 p-0 hover:bg-muted focus:ring-2 focus:ring-ring transition-colors"
                    title="Copy IBAN"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-border">
                <span className="text-muted-foreground">{t('payment.bicSwift')}:</span>
                <span className="font-mono text-foreground">{settings.bank_transfer_bic_swift}</span>
              </div>
              <div className="flex justify-between items-center py-3 px-4 -mx-5 bg-primary/10 rounded-b-lg mt-2">
                <span className="font-semibold text-foreground">{t('payment.amountToTransfer')}:</span>
                <span className="font-bold text-xl text-primary">€{totals.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Invoice preview for company */}
        {isCompany && paymentMethod === "INVOICE" && (
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 text-foreground">
              <Building2 className="w-5 h-5 text-primary" />
              <span className="font-medium">{t('payment.companyInvoiceTitle')}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">{t('payment.companyInvoiceNote')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

type SummaryStepProps = { 
  cart: Cart; 
  settings: StoreSettings; 
  fulfillmentMethod: "SHIPPING" | "PICKUP"; 
  shippingMethods: ShippingMethod[]; 
  shippingMethodId: number | null; 
  shippingContact: ShippingContact; 
  selectedPickup: PickupPoint | null; 
  paymentMethod: PaymentMethodType; 
  isCompany: boolean;
  totals: CheckoutTotals;
  onSuccess: (order: OrderResponse) => void;
};

function SummaryStep({ cart, settings, fulfillmentMethod, shippingMethods, shippingMethodId, shippingContact, selectedPickup, paymentMethod, isCompany, totals, onSuccess }: SummaryStepProps) {
  const { t } = useTranslation('checkout');
  const [processing, setProcessing] = useState(false);
  const selectedMethod = shippingMethods.find((m) => m.id === shippingMethodId);
  const isCttPickup = selectedMethod?.name.toLowerCase() === "ctt pickup point";

  const handlePlaceOrder = async () => {
    setProcessing(true);
    try {
      const idempotencyKey = uuidv4();
      const orderPayload: Record<string, unknown> = {
        fulfillment_method: fulfillmentMethod,
        shipping_contact: shippingContact,
        shipping_method_id: fulfillmentMethod === "SHIPPING" ? shippingMethodId : null,
        payment_method: paymentMethod,
      };
      if (fulfillmentMethod === "SHIPPING" && selectedPickup) {
        orderPayload.pickup_point_code = selectedPickup.code;
        orderPayload.pickup_point_name = selectedPickup.name;
        orderPayload.pickup_point_type = selectedPickup.type || null;
        orderPayload.pickup_point_address_snapshot = { 
          line1: selectedPickup.address.line1, 
          postal_code: selectedPickup.address.postal_code, 
          city: selectedPickup.address.city, 
          country: selectedPickup.address.country 
        };
      }
      const orderResponse = await axiosInstance.post<OrderResponse>("/checkout", orderPayload, { 
        headers: { "Idempotency-Key": idempotencyKey } 
      });
      toast.success(t("messages.orderPlacedSimple"));
      onSuccess(orderResponse.data);
    } catch (error: unknown) { 
      toast.error(getApiErrorMessage(error, t("errors.orderFailed"))); 
    }
    finally { setProcessing(false); }
  };

  return (
    <Card>
      <CardHeader><CardTitle>{t('summary.reviewTitle')}</CardTitle></CardHeader>
      <CardContent className="space-y-6">
        {/* Company Invoice Banner */}
        {isCompany && paymentMethod === "INVOICE" && (
          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-blue-800 dark:text-blue-300">
              <Building2 className="w-5 h-5" />
              <span className="font-medium">{t('summary.companyInvoiceTitle')}</span>
            </div>
            <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
              {t('summary.companyInvoiceNote')}
            </p>
          </div>
        )}

        {/* Bank Transfer Notice */}
        {!isCompany && paymentMethod === "BANK_TRANSFER" && (
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">{t('summary.bankTransferNoticeTitle')}</span>
            </div>
            <ul className="text-sm text-amber-700 dark:text-amber-400 space-y-1 ml-7">
              <li>• {t('summary.bankTransferNotice1')}</li>
              <li>• {t('summary.bankTransferNotice2')}</li>
              <li>• {t('summary.bankTransferNotice3')}</li>
            </ul>
          </div>
        )}

        {/* Order Items */}
        <div>
          <h3 className="font-medium mb-2">{t('summary.items')}</h3>
          <div className="space-y-2">
            {cart.items.map((item) => (
              <div key={item.id} className="flex justify-between text-sm">
                <span>{getCartItemLabel(item)} (x{item.quantity})</span>
                <span>€{item.subtotal.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Fulfillment Info */}
        <div>
          <h3 className="font-medium mb-2">{t('summary.fulfillmentTitle')}</h3>
          <p className="text-sm">{fulfillmentMethod === "SHIPPING" ? t('summary.shipping') : t('summary.pickupInStore')}</p>
          {fulfillmentMethod === "SHIPPING" && selectedMethod && (
            <p className="text-sm text-muted-foreground">{selectedMethod.name} - €{selectedMethod.price.toFixed(2)}</p>
          )}
          {fulfillmentMethod === "SHIPPING" && isCttPickup && selectedPickup && (
            <div className="mt-2 text-sm">
              <p>{selectedPickup.name}</p>
              <p className="text-muted-foreground">{selectedPickup.address.line1}, {selectedPickup.address.postal_code} {selectedPickup.address.city}</p>
            </div>
          )}
          {fulfillmentMethod === "SHIPPING" && !isCttPickup && (
            <div className="mt-2 text-sm text-muted-foreground">
              <p>{shippingContact.full_name}</p>
              <p>{shippingContact.address_line1}</p>
              <p>{shippingContact.city}, {shippingContact.postal_code}</p>
            </div>
          )}
          {fulfillmentMethod === "PICKUP" && (
            <div className="mt-2 text-sm text-muted-foreground">
              <p>{t('summary.contact')}: {shippingContact.full_name}</p>
              <p>{t('summary.email')}: {shippingContact.email}</p>
              {settings?.pickup_location && <p>{t('summary.location')}: {settings.pickup_location}</p>}
            </div>
          )}
        </div>

        {/* Payment Method */}
        <div>
          <h3 className="font-medium mb-2">{t('summary.paymentMethodTitle')}</h3>
          <p className="text-sm">{paymentMethod === "INVOICE" ? t('summary.invoicePayment') : t('summary.bankTransferPayment')}</p>
        </div>

        {/* Tax Info */}
        <div>
          <h3 className="font-medium mb-2">{t('summary.taxTitle')}</h3>
          <p className="text-sm text-muted-foreground">{t('summary.taxInfo', { rate: (settings.tax_rate * 100).toFixed(1) })}</p>
        </div>

        {/* Place Order Button */}
        <Button 
          onClick={handlePlaceOrder} 
          disabled={processing} 
          className="w-full"
          size="lg"
        >
          {processing ? t('summary.processing') : t('summary.placeOrderWithAmount', { amount: formatCurrency(totals.total) })}
        </Button>
      </CardContent>
    </Card>
  );
}

type ConfirmationStepProps = { order: OrderResponse; settings: StoreSettings | null; isCompany: boolean; onContinueShopping: () => void; };

// --- START EDIT: Hardened ConfirmationStep with defensive rendering and safe number parsing ---
function ConfirmationStep({ order, settings, isCompany, onContinueShopping }: ConfirmationStepProps) {
  const { t } = useTranslation('checkout');
  // Defensive check: if order data is missing critical fields, show a friendly message
  if (!order || !order.order_code) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-amber-500" />
              <CardTitle>{t('confirmation.errorTitle')}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-muted-foreground">{t('confirmation.errorNote')}</p>
            <Button onClick={onContinueShopping} className="w-full">{t('confirmation.continueShopping')}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Normalize total_price to a number for safe .toFixed() usage
  const totalPrice = toSafeNumber(order.total_price);

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-green-600" />
            <CardTitle>{t('confirmation.successTitle')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Prominent Order Code Display */}
          <div className="text-center py-6 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/50 dark:to-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">{t('confirmation.yourOrderNumber')}</p>
            <div className="flex items-center justify-center gap-2">
              <p className="text-4xl font-bold text-blue-700 dark:text-blue-300">{order.order_code}</p>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => copyToClipboard(order.order_code, t("messages.copiedToClipboard"))}
                className="hover:bg-blue-200 dark:hover:bg-blue-800"
              >
                <Copy className="w-5 h-5" />
              </Button>
            </div>
            <p className="text-xs text-blue-500 dark:text-blue-500 mt-2">{t('confirmation.keepForRecords')}</p>
          </div>

          {/* Bank Transfer Instructions */}
          {!isCompany && order.payment_method === "BANK_TRANSFER" && (
            <>
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">{t('confirmation.onHoldTitle')}</span>
                </div>
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  {t('confirmation.onHoldNote', { code: order.order_code })}
                </p>
              </div>
              {settings && (
                <div className="bg-card border border-border rounded-lg p-5 space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Banknote className="w-5 h-5 text-primary" />
                    <h4 className="font-semibold text-foreground text-lg">{t('confirmation.bankTransferDetailsTitle')}</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center py-2.5 border-b border-border">
                      <span className="text-muted-foreground">{t('payment.entity')}:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-foreground">{settings.bank_transfer_entity}</span>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(settings.bank_transfer_entity || "", t("messages.copiedToClipboard"))} className="h-7 w-7 p-0 hover:bg-muted focus:ring-2 focus:ring-ring" title="Copy Entity"><Copy className="w-3 h-3" /></Button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center py-2.5 border-b border-border">
                      <span className="text-muted-foreground">{t('payment.iban')}:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-foreground">{settings.bank_transfer_iban}</span>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(settings.bank_transfer_iban || "", t("messages.copiedToClipboard"))} className="h-7 w-7 p-0 hover:bg-muted focus:ring-2 focus:ring-ring" title="Copy IBAN"><Copy className="w-3 h-3" /></Button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center py-2.5 border-b border-border">
                      <span className="text-muted-foreground">{t('payment.bicSwift')}:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-foreground">{settings.bank_transfer_bic_swift}</span>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(settings.bank_transfer_bic_swift || "", t("messages.copiedToClipboard"))} className="h-7 w-7 p-0 hover:bg-muted focus:ring-2 focus:ring-ring" title="Copy BIC/SWIFT"><Copy className="w-3 h-3" /></Button>
                      </div>
                    </div>
                    <hr className="my-2 border-border" />
                    <div className="flex justify-between items-center py-2.5">
                      <span className="text-muted-foreground">{t('payment.reference')}:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-foreground">{order.order_code}</span>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(order.order_code, t("messages.copiedToClipboard"))} className="h-7 w-7 p-0 hover:bg-muted focus:ring-2 focus:ring-ring" title="Copy Reference"><Copy className="w-3 h-3" /></Button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center py-3 px-4 -mx-5 bg-primary/10 rounded-b-lg mt-2">
                      <span className="font-semibold text-foreground">{t('confirmation.amountLabel')}:</span>
                      <span className="font-bold text-xl text-primary">€{totalPrice.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Company Invoice Confirmation */}
          {isCompany && order.payment_method === "INVOICE" && (
            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2 text-blue-800 dark:text-blue-300">
                <Building2 className="w-5 h-5" />
                <span className="font-medium">{t('confirmation.invoiceConfirmedTitle')}</span>
              </div>
              <p className="text-sm text-blue-700 dark:text-blue-400">
                {t('confirmation.invoiceConfirmedNote', { amount: `€${totalPrice.toFixed(2)}` })}
              </p>
            </div>
          )}

          {/* Order Status Info */}
          <div className="bg-card border border-border rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">{t('confirmation.orderStatusLabel')}</span>
              <span className="text-sm font-medium text-foreground">
                {order.payment_status === "AWAITING_RECEIPT"
                  ? t('confirmation.statusOnHold')
                  : order.payment_status === "UNPAID"
                  ? t('confirmation.statusPendingInvoice')
                  : t('confirmation.statusPending')}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-muted-foreground">{t('confirmation.fulfillmentLabel')}</span>
              <span className="text-sm font-medium text-foreground">
                {order.fulfillment_method === "PICKUP" ? t('confirmation.fulfillmentPickup') : t('confirmation.fulfillmentShipping')}
              </span>
            </div>
          </div>

          <Button onClick={onContinueShopping} className="w-full">{t('confirmation.continueShopping')}</Button>
        </CardContent>
      </Card>
    </div>
  );
}
// --- END EDIT ---

type OrderSummaryProps = { totals: CheckoutTotals; settings: StoreSettings | null; };

function OrderSummary({ totals, settings }: OrderSummaryProps) {
  const { t } = useTranslation('checkout');
  return (
    <Card>
      <CardHeader><CardTitle>{t('summary.title')}</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <div className="flex justify-between"><span>{t('summary.subtotal')} (excl. {t('summary.tax')})</span><span>{formatCurrency(totals.itemsSubtotal)}</span></div>
        <div className="flex justify-between"><span>{t('summary.shipping')}</span><span>{formatCurrency(totals.shipping)}</span></div>
        <div className="flex justify-between"><span>{t('summary.tax')} ({(settings?.tax_rate ? settings.tax_rate * 100 : 23).toFixed(1)}%)</span><span>{formatCurrency(totals.tax)}</span></div>
        <hr />
        <div className="flex justify-between font-medium text-lg"><span>{t('summary.total')}</span><span>{formatCurrency(totals.total)}</span></div>
        <p className="text-xs text-muted-foreground mt-2">{t('summary.tax')} applied to items and shipping</p>
      </CardContent>
    </Card>
  );
}
